import { AxiosResponse } from 'axios';
import { useQuery, UseQueryResult } from 'react-query';
import { ScoreTypeEnum, WorkoutLevelEnum } from 'types/enums';
import { client } from 'utils/api-client';
import { encodeUrlParams } from 'utils/functions';

// Same polling cadence as useWorkoutAthleteResults — the board is computed per request,
// not materialized, so freshness comes from polling rather than invalidation alone.
const POLL_INTERVAL_MS = 60_000;

export type LeaderboardGenderFilter = 'all' | 'men' | 'women';
export type LeaderboardLevelFilter = 'all' | WorkoutLevelEnum;

export interface LeaderboardApiEntry {
  rank: number;
  athleteId: string;
  name: string;
  initials: string;
  score: string;
  display: string;
}

export interface LeaderboardApiGroup {
  level: WorkoutLevelEnum | null;
  entries: LeaderboardApiEntry[];
}

export interface LeaderboardResponse {
  sectionMovement: {
    id: string;
    movement: string;
    section: string;
    scoreType: ScoreTypeEnum;
    unit: string;
  };
  appliedFilters: {
    sessionId: string | null;
    gender: LeaderboardGenderFilter;
    level: LeaderboardLevelFilter;
  };
  total: number;
  groups: LeaderboardApiGroup[];
}

interface UseWorkoutLeaderboardParams {
  workout: string | undefined;
  workoutSectionMovement: string | undefined;
  sessionId?: string | null;
  gender?: LeaderboardGenderFilter;
  level?: LeaderboardLevelFilter;
  enabled?: boolean;
}

function useWorkoutLeaderboard({
  workout,
  workoutSectionMovement,
  sessionId = null,
  gender = 'all',
  level = 'all',
  enabled = true,
}: UseWorkoutLeaderboardParams): UseQueryResult<AxiosResponse<LeaderboardResponse>> {
  return useQuery(
    ['workout-leaderboard', workout, workoutSectionMovement, sessionId, gender, level],
    () =>
      client.get<LeaderboardResponse>(
        `workout-leaderboard/?${encodeUrlParams({
          workout: workout as string,
          workout_section_movement: workoutSectionMovement as string,
          ...(sessionId ? { session_id: sessionId } : {}),
          gender,
          level,
        })}`,
      ),
    {
      enabled: enabled && !!workout && !!workoutSectionMovement,
      refetchInterval: POLL_INTERVAL_MS,
      refetchIntervalInBackground: false,
    },
  );
}

export default useWorkoutLeaderboard;
